/**
 * 전체화면 창(window) 스택 store — 프레임워크·도메인 독립의 순수 상태 머신.
 *
 * Owns:
 * - 떠 있는 창 엔트리 목록(최대 MAX_WINDOWS)과 "지금 어느 하나가 전체화면으로 보이는가
 *   (activeId)"라는 단일 활성 불변식.
 * - open/setActive/minimize/close 의 stacking 정책: 동시 1개만 표시, 새로 열면 직전 활성 자동
 *   최소화, cap 초과 시 차단(null 반환, bypassCap 예외), 같은 (kind,key) 중복 방지, singleton
 *   kind 원자 교체.
 * - 엔트리 closable 불변식: closable=false 인 엔트리는 close 로 못 닫는다(진행 중 업로드 창처럼
 *   완료/폐기 전 닫기 금지용). 닫으려면 먼저 update 로 closable=true 로 풀어야 한다.
 * - 엔트리별 onClose lifecycle 훅(닫힘 시 1회 호출 후 폐기).
 * - 엔트리별 닫기 guard 와 확인 대기(`closeRequest`): 사람이 닫기를 요청했는데 guard 가 참이면 닫지 않고
 *   확인을 기다린다. guard 는 콘텐츠가 등록한다(저장하지 않은 편집 등). 세션 끝(`clear`)은 guard 를 보지 않는다.
 *
 * Does not own:
 * - cap 초과 안내(toast 등)·kind 집합·싱글턴 인스턴스 — 소비처(앱)가 소유한다.
 * - 닫기 확인의 문구와 그리기 — 호스트(`WindowStackHost`)가 소유한다.
 * - 각 kind 의 콘텐츠 렌더링과 params 해석 — 호스트가 renderWindow 로 주입한다.
 *
 * kind 는 불투명 문자열로 다룬다(이 store 는 해석하지 않는다). 외부 스토어(subscribe/getSnapshot)로만
 * 노출해, 창을 최소화한 뒤 화면을 옮겨도 스택이 유지·복원되게 한다.
 */

import type { ReactNode } from "react";

// 동시에 떠 있을 수 있는 전체화면 창 수 상한. 초과하면 open 이 null 을 돌려준다(열기 차단).
export const MAX_WINDOWS = 5;

export type WindowEntry = {
  readonly id: string;
  // 창 종류(소비처가 의미를 안다 — 호스트 렌더 분기 키). store 는 불투명 문자열로 취급.
  readonly kind: string;
  // kind 내 중복 방지 키(예: datasetUuid). 같은 (kind,key)면 새로 열지 않고 기존 것을 활성화한다.
  readonly key: string;
  // dock 라벨(본문). 소비처가 데이터 적재 후 update 로 보정한다.
  readonly title: string;
  // dock 표시 슬롯. 없으면 title 을 표시한다. 소비처가 rich label 이 필요할 때만 채운다.
  readonly label: ReactNode | null;
  // dock eyebrow(보조 라벨). 없으면 null.
  readonly subtitle: string | null;
  // 닫기 허용 여부. false 면 close 가 무시된다(진행 중 업로드처럼 완료/폐기 전 닫기 금지). 소비처가
  // 상태가 terminal 로 바뀌면 update 로 true 로 푼다. 기본 true(대다수 창은 언제든 닫힌다).
  readonly closable: boolean;
  // 콘텐츠가 읽는 kind별 payload(store 는 해석하지 않는다 — 소비처가 캐스팅한다).
  readonly params: unknown;
};

export type WindowStackSnapshot = {
  readonly entries: readonly WindowEntry[];
  // 전체화면으로 보이는 엔트리 id. null 이면 모두 최소화(배경+dock 노출).
  readonly activeId: string | null;
  // 닫기를 확인받는 중인 엔트리 id. guard 가 참이라 닫지 않고 기다린다. 없으면 null.
  readonly closeRequest: string | null;
};

export type OpenWindowInput = {
  kind: string;
  key: string;
  title: string;
  label?: ReactNode;
  subtitle?: string;
  params: unknown;
  // 닫기 허용 여부(기본 true). false 면 열린 뒤에도 close 가 무시된다 — 진행 중 업로드 창처럼
  // 완료/폐기 전 닫기를 막는 용도. 소비처가 terminal 로 바뀌면 update({closable:true})로 푼다.
  closable?: boolean;
  // 전역 cap(MAX_WINDOWS)을 무시하고 무조건 연다. 창 존재가 불변식인 kind(예: 진행 중 업로드 —
  // 서버 잡은 반드시 대응 창을 가져야 함) 전용. 자체 상한은 소비처가 별도로 건다.
  bypassCap?: boolean;
  // 같은 kind 의 다른 엔트리를 원자적으로 교체한다(항상 1개만 유지하는 kind 용, 예: review).
  singleton?: boolean;
  // 엔트리가 닫힐 때(close/singleton 교체) 1회 호출. 목록 새로고침 등 정리에 쓴다.
  onClose?: () => void;
};

type WindowEntryPatch = Partial<
  Pick<WindowEntry, "title" | "label" | "subtitle" | "params" | "closable">
>;

// 빈 스냅샷 — 매 close 마다 새 객체를 만들지 않도록 단일 인스턴스를 재사용한다(getSnapshot 안정성).
const emptySnapshot: WindowStackSnapshot = { entries: [], activeId: null, closeRequest: null };

export class WindowStackManager {
  private readonly listeners = new Set<() => void>();
  private snapshot: WindowStackSnapshot = emptySnapshot;
  // 엔트리별 onClose 훅 — snapshot 밖에 둔다(렌더 트리거·identity 와 무관).
  private readonly closeHandlers = new Map<string, () => void>();
  // 엔트리별 닫기 guard — 참이면 사람이 요청한 닫기를 확인받는다. snapshot 밖에 둔다.
  private readonly closeGuards = new Map<string, () => boolean>();
  private seq = 0;
  private clearing = false;

  getSnapshot = (): WindowStackSnapshot => this.snapshot;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  // kind 의 첫 엔트리(singleton kind 조회용). 여러 개면 첫 번째.
  entryOfKind = (kind: string): WindowEntry | undefined =>
    this.snapshot.entries.find((entry) => entry.kind === kind);

  /**
   * 새 전체화면 창을 활성 상태로 연다. 열린 엔트리 id 를, cap 초과로 못 열면 null 을 돌려준다.
   * - 같은 (kind,key)가 이미 있으면 새로 열지 않고 그 엔트리를 활성화(복원)하고 id 를 돌려준다.
   * - singleton kind 면 같은 kind 의 기존 엔트리를 원자적으로 교체한다(직전 onClose 호출).
   * - bypassCap=true 면 cap 을 무시하고 무조건 연다(창 존재가 불변식인 kind 전용).
   * - 그 외엔 push 하고 activeId 를 새 엔트리로 둔다(직전 활성은 자동 최소화).
   */
  open = (input: OpenWindowInput): string | null => {
    if (this.clearing) {
      return null;
    }
    const entries = this.snapshot.entries;
    const existing = entries.find((entry) => entry.kind === input.kind && entry.key === input.key);
    if (existing !== undefined) {
      this.setActive(existing.id);
      return existing.id;
    }

    let base = entries;
    let replaced: readonly WindowEntry[] = [];
    if (input.singleton === true) {
      replaced = entries.filter((entry) => entry.kind === input.kind);
      if (replaced.length > 0) {
        base = entries.filter((entry) => entry.kind !== input.kind);
      }
    }

    if (input.bypassCap !== true && base.length >= MAX_WINDOWS) {
      return null;
    }

    // cap 통과 후에만 교체 대상의 onClose 를 호출한다(부수효과를 commit 과 원자적으로 묶는다).
    for (const entry of replaced) {
      this.runCloseHandler(entry.id);
    }

    const id = `win-${(this.seq += 1)}`;
    const entry: WindowEntry = {
      id,
      kind: input.kind,
      key: input.key,
      title: input.title,
      label: input.label ?? null,
      subtitle: input.subtitle ?? null,
      closable: input.closable ?? true,
      params: input.params,
    };
    if (input.onClose !== undefined) {
      this.closeHandlers.set(id, input.onClose);
    }
    this.commit([...base, entry], id);
    return id;
  };

  // 해당 엔트리를 전체화면으로(복원/전환). 직전 활성은 자동으로 최소화된다(activeId 단일).
  setActive = (id: string): void => {
    if (this.snapshot.activeId === id) {
      return;
    }
    if (!this.snapshot.entries.some((entry) => entry.id === id)) {
      return;
    }
    this.commit(this.snapshot.entries, id);
  };

  // 활성 창을 최소화한다(activeId=null → 배경+dock 노출). id 지정 시 그 엔트리가 활성일 때만.
  minimize = (id?: string): void => {
    if (this.snapshot.activeId === null) {
      return;
    }
    if (id !== undefined && this.snapshot.activeId !== id) {
      return;
    }
    this.commit(this.snapshot.entries, null);
  };

  /**
   * 사람이 요청한 닫기(닫기 버튼 · Escape · 작업표시줄). guard 가 참이면 닫지 않고 `closeRequest` 를 세운다.
   * 호스트가 확인을 받아 `confirmClose` 또는 `cancelClose` 를 부른다. guard 가 없거나 거짓이면 바로 닫는다.
   */
  requestClose = (id: string): void => {
    const target = this.snapshot.entries.find((entry) => entry.id === id);
    if (target === undefined || !target.closable) {
      return;
    }
    if (this.closeGuards.get(id)?.() === true) {
      if (this.snapshot.closeRequest !== id) {
        this.commit(this.snapshot.entries, this.snapshot.activeId, id);
      }
      return;
    }
    this.close(id);
  };

  // 확인받은 닫기를 한다. 기다리는 요청이 없으면 무시한다.
  confirmClose = (): void => {
    const id = this.snapshot.closeRequest;
    if (id !== null) {
      this.close(id);
    }
  };

  // 닫기 확인을 거둔다. 창은 그대로다.
  cancelClose = (): void => {
    if (this.snapshot.closeRequest !== null) {
      this.commit(this.snapshot.entries, this.snapshot.activeId, null);
    }
  };

  // 엔트리의 닫기 guard 를 등록한다. null 이면 지운다. 엔트리가 닫히면 함께 지운다.
  setCloseGuard = (id: string, guard: (() => boolean) | null): void => {
    if (guard === null) {
      this.closeGuards.delete(id);
    } else {
      this.closeGuards.set(id, guard);
    }
  };

  // 엔트리를 닫고(제거) onClose 를 호출한다. 활성이었으면 activeId 를 비운다. guard 는 보지 않는다.
  // closable=false 엔트리는 닫지 않는다(진행 중 업로드 등 — 먼저 update 로 풀어야 한다).
  close = (id: string): void => {
    const target = this.snapshot.entries.find((entry) => entry.id === id);
    if (target !== undefined && !target.closable) {
      return;
    }
    const entries = this.snapshot.entries.filter((entry) => entry.id !== id);
    if (entries.length === this.snapshot.entries.length) {
      return;
    }
    const activeId = this.snapshot.activeId === id ? null : this.snapshot.activeId;
    const closeRequest = this.snapshot.closeRequest === id ? null : this.snapshot.closeRequest;
    this.commit(entries, activeId, closeRequest);
    this.runCloseHandler(id);
  };

  /**
   * 세션에 속한 모든 창을 강제로 닫는다.
   *
   * 진행 중 업로드처럼 `closable=false`인 창도 세션 경계에서는 남길 수 없으므로 제거한다. 각
   * `onClose`는 정확히 한 번 실행하고, 하나가 실패해도 나머지를 끝까지 실행한 뒤 첫 오류를 전달한다.
   */
  clear = (): void => {
    if (this.snapshot.entries.length === 0) {
      return;
    }
    const entries = [...this.snapshot.entries];
    this.clearing = true;
    let firstError: unknown = null;
    try {
      try {
        this.commit([], null);
      } catch (error) {
        firstError = error;
      }
      for (const entry of entries) {
        try {
          this.runCloseHandler(entry.id);
        } catch (error) {
          firstError ??= error;
        }
      }
    } finally {
      this.clearing = false;
    }
    if (firstError !== null) {
      throw firstError;
    }
  };

  // dock 라벨/콘텐츠 params 보정(예: 데이터 적재 후 제목, 리뷰 스텝 변경). 바뀐 게 없으면 무시한다.
  update = (id: string, patch: WindowEntryPatch): void => {
    let changed = false;
    const entries = this.snapshot.entries.map((entry) => {
      if (entry.id !== id) {
        return entry;
      }
      const next = { ...entry, ...patch };
      if (
        next.title === entry.title &&
        next.label === entry.label &&
        next.subtitle === entry.subtitle &&
        next.closable === entry.closable &&
        next.params === entry.params
      ) {
        return entry;
      }
      changed = true;
      return next;
    });
    if (!changed) {
      return;
    }
    this.commit(entries, this.snapshot.activeId);
  };

  private runCloseHandler(id: string): void {
    const handler = this.closeHandlers.get(id);
    this.closeHandlers.delete(id);
    this.closeGuards.delete(id);
    handler?.();
  }

  // 닫기 확인은 엔트리가 남아 있을 때만 유지한다. 인자를 주지 않으면 지금 값을 잇는다.
  private commit(
    entries: readonly WindowEntry[],
    activeId: string | null,
    closeRequest: string | null = this.snapshot.closeRequest,
  ): void {
    const pending = entries.some((entry) => entry.id === closeRequest) ? closeRequest : null;
    this.snapshot =
      entries.length === 0 ? emptySnapshot : { entries, activeId, closeRequest: pending };
    for (const listener of this.listeners) {
      listener();
    }
  }
}
